/**
 * SKOP API Key Management
 *
 * Generate and manage API keys for programmatic access.
 */

export interface ApiKey {
  id: string;
  key: string;
  keyPrefix: string;
  label: string;
  createdAt: number;
  lastUsedAt: number | null;
  usageToday: number;
  usageTotal: number;
  status: 'active' | 'revoked';
}

export interface ApiUsage {
  date: string;
  endpoint: string;
  count: number;
}

export interface ApiKeyStats {
  totalKeys: number;
  activeKeys: number;
  usageToday: number;
  usageThisMonth: number;
  remainingToday: number;
  overageThisMonth: number;
  overageAmountUsd: number;
}

// Rate limits for API tier
export const API_RATE_LIMITS = {
  api: {
    dailyLimit: 10000,
    perMinuteLimit: 300,
    perSecondLimit: 50,
    allowOverage: false,
    overagePriceUsd: 0,
  },
} as const;

// Overage tracking
export interface OverageRecord {
  month: string; // YYYY-MM
  overageCount: number;
  overageAmountUsd: number;
  paid: boolean;
  paidAt?: number;
}

const OVERAGE_KEY = 'skop_api_overage';

// Storage keys
const API_KEYS_KEY = 'skop_api_keys';
const API_USAGE_KEY = 'skop_api_usage';

// ============================================================================
// API Key Management
// ============================================================================

/**
 * Get all API keys for a user
 */
export function getApiKeys(walletAddress: string): ApiKey[] {
  try {
    const stored = localStorage.getItem(`${API_KEYS_KEY}_${walletAddress}`);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch {
    return [];
  }
}

/**
 * Save API keys
 */
function saveApiKeys(walletAddress: string, keys: ApiKey[]): void {
  localStorage.setItem(`${API_KEYS_KEY}_${walletAddress}`, JSON.stringify(keys));
}

/**
 * Create a new API key
 */
export function createApiKey(walletAddress: string, label: string): { key: ApiKey; fullKey: string } {
  const fullKey = generateFullApiKey();
  const keyPrefix = `${fullKey.slice(0, 12)}...`;

  const newKey: ApiKey = {
    id: generateKeyId(),
    key: hashApiKey(fullKey), // Store hashed version
    keyPrefix,
    label,
    createdAt: Date.now(),
    lastUsedAt: null,
    usageToday: 0,
    usageTotal: 0,
    status: 'active',
  };

  const keys = getApiKeys(walletAddress);
  keys.push(newKey);
  saveApiKeys(walletAddress, keys);

  // Return the full key only once (won't be stored in plain text)
  return { key: newKey, fullKey };
}

/**
 * Revoke an API key
 */
export function revokeApiKey(walletAddress: string, keyId: string): boolean {
  const keys = getApiKeys(walletAddress);
  const key = keys.find(k => k.id === keyId);
  if (!key) return false;

  key.status = 'revoked';
  saveApiKeys(walletAddress, keys);
  return true;
}

/**
 * Delete an API key
 */
export function deleteApiKey(walletAddress: string, keyId: string): boolean {
  const keys = getApiKeys(walletAddress);
  const index = keys.findIndex(k => k.id === keyId);
  if (index === -1) return false;

  keys.splice(index, 1);
  saveApiKeys(walletAddress, keys);
  return true;
}

/**
 * Update key label
 */
export function updateApiKeyLabel(walletAddress: string, keyId: string, label: string): boolean {
  const keys = getApiKeys(walletAddress);
  const key = keys.find(k => k.id === keyId);
  if (!key) return false;

  key.label = label;
  saveApiKeys(walletAddress, keys);
  return true;
}

/**
 * Record API key usage
 */
export function recordApiKeyUsage(walletAddress: string, keyId: string, endpoint: string): void {
  const keys = getApiKeys(walletAddress);
  const key = keys.find(k => k.id === keyId);
  if (!key) return;

  key.lastUsedAt = Date.now();
  key.usageToday += 1;
  key.usageTotal += 1;
  saveApiKeys(walletAddress, keys);

  // Also record in usage log
  recordUsage(walletAddress, endpoint);
}

/**
 * Reset daily usage (called at start of new day)
 */
export function resetDailyUsage(walletAddress: string): void {
  const keys = getApiKeys(walletAddress);
  keys.forEach(key => { key.usageToday = 0; });
  saveApiKeys(walletAddress, keys);
}

/**
 * Get API key stats
 */
export function getApiKeyStats(walletAddress: string): ApiKeyStats {
  const keys = getApiKeys(walletAddress);
  const activeKeys = keys.filter(k => k.status === 'active');
  const usageToday = keys.reduce((sum, k) => sum + k.usageToday, 0);
  const usageTotal = keys.reduce((sum, k) => sum + k.usageTotal, 0);
  const limits = API_RATE_LIMITS.api;
  const overage = getCurrentMonthOverage(walletAddress);

  return {
    totalKeys: keys.length,
    activeKeys: activeKeys.length,
    usageToday,
    usageThisMonth: usageTotal,
    remainingToday: Math.max(0, limits.dailyLimit - usageToday),
    overageThisMonth: overage.overageCount,
    overageAmountUsd: overage.overageAmountUsd,
  };
}

// ============================================================================
// Overage Management
// ============================================================================

/**
 * Get current month string
 */
function getCurrentMonth(): string {
  return new Date().toISOString().slice(0, 7); // YYYY-MM
}

/**
 * Get overage records for a user
 */
export function getOverageRecords(walletAddress: string): OverageRecord[] {
  try {
    const stored = localStorage.getItem(`${OVERAGE_KEY}_${walletAddress}`);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch {
    return [];
  }
}

/**
 * Save overage records
 */
function saveOverageRecords(walletAddress: string, records: OverageRecord[]): void {
  localStorage.setItem(`${OVERAGE_KEY}_${walletAddress}`, JSON.stringify(records));
}

/**
 * Get current month's overage
 */
export function getCurrentMonthOverage(walletAddress: string): OverageRecord {
  const records = getOverageRecords(walletAddress);
  const currentMonth = getCurrentMonth();

  const existing = records.find(r => r.month === currentMonth);
  if (existing) return existing;

  return {
    month: currentMonth,
    overageCount: 0,
    overageAmountUsd: 0,
    paid: false,
  };
}

/**
 * Record an overage request (called when user exceeds daily limit)
 */
export function recordOverage(walletAddress: string): { allowed: boolean; charged: number } {
  const limits = API_RATE_LIMITS.api;

  if (!limits.allowOverage) {
    return { allowed: false, charged: 0 };
  }

  const records = getOverageRecords(walletAddress);
  const currentMonth = getCurrentMonth();

  let record = records.find(r => r.month === currentMonth);
  if (!record) {
    record = {
      month: currentMonth,
      overageCount: 0,
      overageAmountUsd: 0,
      paid: false,
    };
    records.push(record);
  }

  record.overageCount += 1;
  record.overageAmountUsd += limits.overagePriceUsd;

  saveOverageRecords(walletAddress, records);

  return { allowed: true, charged: limits.overagePriceUsd };
}

/**
 * Check if request is allowed (within limits or overage allowed)
 */
export function checkRequestAllowed(
  walletAddress: string
): { allowed: boolean; isOverage: boolean; message?: string } {
  const stats = getApiKeyStats(walletAddress);
  const limits = API_RATE_LIMITS.api;

  // Within daily limit
  if (stats.usageToday < limits.dailyLimit) {
    return { allowed: true, isOverage: false };
  }

  // Over limit - check if overage is allowed
  if (limits.allowOverage) {
    return {
      allowed: true,
      isOverage: true,
      message: `Overage: $${limits.overagePriceUsd.toFixed(4)}/request`
    };
  }

  return {
    allowed: false,
    isOverage: false,
    message: `Daily limit of ${limits.dailyLimit.toLocaleString()} requests reached`
  };
}

/**
 * Get unpaid overage balance
 */
export function getUnpaidOverageBalance(walletAddress: string): number {
  const records = getOverageRecords(walletAddress);
  return records
    .filter(r => !r.paid)
    .reduce((sum, r) => sum + r.overageAmountUsd, 0);
}

/**
 * Mark overage as paid
 */
export function markOveragePaid(walletAddress: string, month: string): boolean {
  const records = getOverageRecords(walletAddress);
  const record = records.find(r => r.month === month);

  if (!record) return false;

  record.paid = true;
  record.paidAt = Date.now();
  saveOverageRecords(walletAddress, records);

  return true;
}

/**
 * Get overage summary for billing
 */
export function getOverageSummary(walletAddress: string): {
  currentMonth: OverageRecord;
  unpaidTotal: number;
  history: OverageRecord[];
} {
  const records = getOverageRecords(walletAddress);
  const currentMonth = getCurrentMonthOverage(walletAddress);
  const unpaidTotal = getUnpaidOverageBalance(walletAddress);

  return {
    currentMonth,
    unpaidTotal,
    history: records.slice(-6), // Last 6 months
  };
}

// ============================================================================
// Usage Tracking
// ============================================================================

/**
 * Get usage history
 */
export function getUsageHistory(walletAddress: string, days: number = 7): ApiUsage[] {
  try {
    const stored = localStorage.getItem(`${API_USAGE_KEY}_${walletAddress}`);
    if (!stored) return [];

    const all: ApiUsage[] = JSON.parse(stored);
    const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;

    return all.filter(u => {
      const date = new Date(u.date).getTime();
      return date >= cutoff;
    });
  } catch {
    return [];
  }
}

/**
 * Record usage for an endpoint
 */
function recordUsage(walletAddress: string, endpoint: string): void {
  try {
    const today = new Date().toISOString().split('T')[0];
    const history = getUsageHistory(walletAddress, 30); // Keep 30 days

    const existing = history.find(u => u.date === today && u.endpoint === endpoint);
    if (existing) {
      existing.count += 1;
    } else {
      history.push({ date: today, endpoint, count: 1 });
    }

    localStorage.setItem(`${API_USAGE_KEY}_${walletAddress}`, JSON.stringify(history));
  } catch {
    // Ignore errors
  }
}

/**
 * Get usage by endpoint
 */
export function getUsageByEndpoint(walletAddress: string): Record<string, number> {
  const history = getUsageHistory(walletAddress, 30);
  const byEndpoint: Record<string, number> = {};

  for (const usage of history) {
    byEndpoint[usage.endpoint] = (byEndpoint[usage.endpoint] || 0) + usage.count;
  }

  return byEndpoint;
}

// ============================================================================
// Key Generation
// ============================================================================

/**
 * Generate a full API key
 */
function generateFullApiKey(): string {
  const prefix = 'sk_live_';
  const randomPart = generateRandomString(32);
  return `${prefix}${randomPart}`;
}

/**
 * Generate random string
 */
function generateRandomString(length: number): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  for (let i = 0; i < length; i++) {
    result += chars[array[i] % chars.length];
  }
  return result;
}

/**
 * Generate unique key ID
 */
function generateKeyId(): string {
  return `key_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Synchronous hash for API key storage using FNV-1a
 * For full security, the actual API key validation happens server-side
 */
function hashApiKey(key: string): string {
  // Use a deterministic hash that's still secure enough for client-side storage
  // For full security, the actual API key validation happens server-side
  let hash = 0x811c9dc5; // FNV offset basis
  for (let i = 0; i < key.length; i++) {
    hash ^= key.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193); // FNV prime
  }
  // Convert to unsigned and hex
  const unsigned = hash >>> 0;
  // Create a longer hash by running multiple rounds
  let extended = unsigned.toString(16);
  let current = hash;
  for (let round = 0; round < 3; round++) {
    for (let i = 0; i < key.length; i++) {
      current ^= key.charCodeAt(i) * (round + 2);
      current = Math.imul(current, 0x01000193);
    }
    extended += (current >>> 0).toString(16);
  }
  return `fnv_${extended}`;
}

// ============================================================================
// API Documentation
// ============================================================================

export interface ApiEndpoint {
  method: 'GET' | 'POST';
  path: string;
  description: string;
  parameters?: Array<{
    name: string;
    type: string;
    required: boolean;
    description: string;
  }>;
  response: string;
  example: string;
}

export const API_ENDPOINTS: ApiEndpoint[] = [
  {
    method: 'POST',
    path: '/api/analyze/token',
    description: 'Get full risk analysis for a token',
    parameters: [
      { name: 'address', type: 'string', required: true, description: 'Token mint address' },
      { name: 'depth', type: 'string', required: false, description: 'Analysis depth: "quick" or "full"' },
    ],
    response: 'TokenAnalysis object with risk score, warnings, holder data',
    example: `curl -X POST https://api.skop.site/api/analyze/token \\
  -H "Authorization: Bearer sk_live_xxx" \\
  -H "Content-Type: application/json" \\
  -d '{"address": "TokenMintAddress..."}'`,
  },
  {
    method: 'GET',
    path: '/api/tokens/trending',
    description: 'Get list of trending tokens',
    response: 'Array of TrendingToken objects',
    example: `curl https://api.skop.site/api/tokens/trending \\
  -H "Authorization: Bearer sk_live_xxx"`,
  },
  {
    method: 'GET',
    path: '/api/tokens/search',
    description: 'Search tokens by name or symbol',
    parameters: [
      { name: 'q', type: 'string', required: true, description: 'Search query' },
      { name: 'limit', type: 'number', required: false, description: 'Max results (default: 10)' },
    ],
    response: 'Array of matching tokens',
    example: `curl "https://api.skop.site/api/tokens/search?q=pepe" \\
  -H "Authorization: Bearer sk_live_xxx"`,
  },
  {
    method: 'GET',
    path: '/api/sentiment/:address',
    description: 'Get social sentiment for a token',
    parameters: [
      { name: 'address', type: 'string', required: true, description: 'Token mint address (in URL)' },
    ],
    response: 'TokenSentiment object with score, trend, mentions',
    example: `curl https://api.skop.site/api/sentiment/TokenMintAddress \\
  -H "Authorization: Bearer sk_live_xxx"`,
  },
  {
    method: 'POST',
    path: '/api/simulate/transaction',
    description: 'Simulate a swap transaction',
    parameters: [
      { name: 'tokenAddress', type: 'string', required: true, description: 'Token to trade' },
      { name: 'action', type: 'string', required: true, description: '"buy" or "sell"' },
      { name: 'amount', type: 'number', required: true, description: 'Amount in SOL (buy) or tokens (sell)' },
      { name: 'slippageBps', type: 'number', required: false, description: 'Slippage in basis points' },
    ],
    response: 'SimulationResult with expected output, fees, risks',
    example: `curl -X POST https://api.skop.site/api/simulate/transaction \\
  -H "Authorization: Bearer sk_live_xxx" \\
  -H "Content-Type: application/json" \\
  -d '{"tokenAddress": "...", "action": "buy", "amount": 1.0}'`,
  },
];

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format usage number
 */
export function formatUsage(count: number): string {
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
  return count.toString();
}

/**
 * Get status color
 */
export function getStatusColor(status: ApiKey['status']): string {
  return status === 'active' ? '#00ff88' : '#ff3b3b';
}

/**
 * Format last used time
 */
export function formatLastUsed(timestamp: number | null): string {
  if (!timestamp) return 'Never';

  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}
